import { jsx as _jsx } from "react/jsx-runtime";
import { CloseOutlined } from "@ant-design/icons";
import { notification } from "antd";
import { makeAutoObservable } from "mobx";
import { v4 } from "uuid";
import { ConsoleLogger } from "../others/console-logger.js";
import { errorToHumanReadableMessage } from "../util/api-fetch.js";
/**
 * Store to push messages / notifications to frontend user.
 */ class NotificationStore {
    /**
     * Stores also rootStore as reference to gain access ot other stores!
     */ rootStore;
    currentlyDisplayedNotificationKeys = new Set();
    log = new ConsoleLogger(this);
    /**
     * Creates an instance of NotificationStore.
     * @param {RootStore} rootStore
     */ constructor(rootStore){
        this.rootStore = rootStore;
        makeAutoObservable(this, {
            rootStore: false
        }, {
            autoBind: true
        });
    }
    add(...notifications) {
        const notificationKeys = new Set();
        if (notifications.length > 0) {
            for (const { message, type, title, closable, duration, placement } of notifications.filter((n)=>n)){
                if (type && message) {
                    const key = `${type}_${message}_${v4()}`;
                    const useFn = type === "neutral" ? "open" : type;
                    // eslint-disable-next-line internal-rulesdir/no-disallowed-import-method
                    notification[useFn]({
                        message: title,
                        description: message,
                        closeIcon: closable === true ? /*#__PURE__*/ _jsx(CloseOutlined, {
                            size: 16
                        }) : null,
                        duration: duration || 0,
                        placement: placement || "topRight",
                        key,
                        onClose: ()=>this.remove(key),
                        style: {
                            paddingRight: "40px",
                            width: "460px"
                        }
                    });
                    notificationKeys.add(key);
                    this.currentlyDisplayedNotificationKeys.add(key);
                }
            }
        }
        return notificationKeys;
    }
    addFromServerError(error, t) {
        const validationErrors = error;
        if (Array.isArray(validationErrors)) {
            try {
                const notifications = validationErrors.map((validationError)=>{
                    return {
                        type: "error",
                        message: validationError.message,
                        closable: true
                    };
                });
                this.add(...notifications);
            } catch (error) {
                // nothing -> wrong error format
                this.log.error("Unabled to display errors as notification: ", {
                    validationErrors
                });
                // try another notification format
                this.addRequestError(error, t);
            }
        } else {
            this.addRequestError(error, t);
        }
    }
    addRequestError(error, t) {
        const errorMessage = errorToHumanReadableMessage(error);
        this.add({
            type: "error",
            title: t("Request Issues"),
            message: t("Please check your internet connection or contact your technical support if the problem persists. Technical details: {{details}}", {
                details: errorMessage
            }),
            closable: true
        });
    }
    /**
     * Removes a single notification key, if registered in this store.
     */ remove(notificationKey) {
        if (this.currentlyDisplayedNotificationKeys.delete(notificationKey)) {
            // was registered, closing now
            // eslint-disable-next-line internal-rulesdir/no-disallowed-import-method
            notification.destroy(notificationKey);
            this.log.dev(`Removed key ${notificationKey}`);
        }
    }
    /**
     * Removes all currently registered notifications via key.
     */ clearAll() {
        if (this.currentlyDisplayedNotificationKeys.size > 0) {
            this.log.dev("Clear all triggered.");
            const currentCopy = new Set(this.currentlyDisplayedNotificationKeys);
            for (const key of currentCopy){
                this.remove(key);
            }
        }
    }
}
export { NotificationStore };
